import clsx from "clsx";
import { useState, useEffect } from "react";

interface FormFieldProps {
  type?: "text" | "textarea";
  label?: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  validate?: (value: string) => string | null;
  required?: boolean;
  className?: string;
}

export default function FormField({
  type = "text",
  label,
  placeholder,
  value,
  onChange,
  onBlur,
  error: externalError,
  validate,
  required = false,
  className = "",
}: FormFieldProps) {
  const [touched, setTouched] = useState(false);
  const [internalError, setInternalError] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);

  const error = externalError || internalError;
  const showError = touched && error;
  const hasValue = value.trim().length > 0;
  const floatingLabel = label || placeholder;
  const showFloatingLabel = isFocused || hasValue;

  useEffect(() => {
    if (touched && validate) {
      const validationError = validate(value);
      setInternalError(validationError || null);
    }
  }, [value, touched, validate]);

  const handleBlur = () => {
    setTouched(true);
    setIsFocused(false);
    onBlur?.();
    if (validate) {
      const validationError = validate(value);
      setInternalError(validationError || null);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const baseInputClasses = `
    w-full text-[#222] text-base border transition-all duration-200 rounded-md
    bg-white
    focus:outline-none focus:shadow-none
    ${
      showError
        ? "border-red-400 focus:border-red-500"
        : "border-[#ddd] hover:border-[#0f5f95] focus:border-[#0f5f95]"
    }
    ${className}
  `;

  const inputClasses = `
    ${baseInputClasses} 
    h-[44px] px-[10px]
    ${showFloatingLabel ? "pt-[-2px]" : "pt-0"}
    ${showFloatingLabel ? "" : "leading-[44px]"}
    placeholder:text-[#999]
    transition-all duration-500 ease-in-out
    ${showFloatingLabel ? "placeholder:opacity-0" : "placeholder:opacity-100"}
  `;

  const textareaClasses = `
    ${baseInputClasses} 
    leading-6 min-h-[180px] p-[10px] resize-none
    ${showFloatingLabel ? "pt-[26px]" : ""}
    placeholder:text-[#999]
    ${showFloatingLabel ? "placeholder:opacity-0" : "placeholder:opacity-100"}
  `;

  return (
    <div className="relative">
      {/* Floating Label */}
      <label
        className={`
          absolute left-[10px] pointer-events-none transition-all duration-300 ease-in-out
          ${
            showFloatingLabel
              ? "top-[-8px] text-sm bg-linear-to-b from-[#f2f4f6ff] to-[#ffffffff] px-1"
              : "top-[-2px] text-sm bg-linear-to-b from-[#f2f4f600] to-[#ffffff00] px-1"
          }
          ${
            showFloatingLabel
              ? showError
                ? "text-red-500"
                : isFocused
                ? "text-[#0f5f95]"
                : "text-[#0f5f95]"
              : "text-[#999] opacity-0"
          }
        `}
      >
        {floatingLabel}
        {/* {required && !showFloatingLabel && <span className="text-red-500 ml-1">*</span>} */}
      </label>

      {/* Input - placeholder visibility controlled by CSS */}
      {type === "textarea" ? (
        <textarea
          className={textareaClasses}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={handleBlur}
          onFocus={handleFocus}
        />
      ) : (
        <input
          type={type}
          className={inputClasses}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={handleBlur}
          onFocus={handleFocus}
        />
      )}

      {/* Error Message */}
      <div
        className={clsx(
          "mt-1 text-sm text-red-500 flex items-center gap-1 animate-fade-in",
          showError && type !== "textarea" ? "opacity-100" : "opacity-0"
        )}
      >
        <span>⚠</span>
        <span>{error}</span>
      </div>
    </div>
  );
}
